import { Button } from "@/components/ui/button";
import { FormError } from "@/forms/form-error";
import { ImageIcon, X } from "lucide-react";
import { logEvent } from "@/service/logs/log";
import { clientPost } from "@/service/client-api/client-api";
import { useContext, useRef, useState } from "react";
import { ResourceContext } from "@/contexts/resource-context";

export const NurseOrMidwifePrescriptionUnlock = () => {
  const [file, setFile] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { logView } = useContext(ResourceContext);

  function getBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      if (!["image/jpg", "image/jpeg", "image/png"].includes(file.type)) {
        alert("Dozwolone formaty pliku to jpeg oraz png.");
        reject("Invalid file type");
        return;
      }

      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  }

  function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setIsSent(false);
      getBase64(selectedFile)
        .then((base64) => {
          setFile(base64);
          setFileName(selectedFile.name);
          setError(null);
        })
        .catch(() => {
          setFile(null);
          setFileName(null);
        });
    }
  }

  function removeFile() {
    setFile(null);
    setFileName(null);
    setIsSent(false);
    setError(null);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  }

  async function upload() {
    if (!file || isSent) return;

    setIsUploading(true);
    try {
      setError(null);
      await clientPost(
        "profile.med-ad-recipient-verification",
        {},
        { medAdVerificationImage: file },
      );
      logEvent("prescription_document_sent", logView);
      setIsSent(true);
    } catch (caughtError: unknown) {
      const maybeAny = caughtError as {
        error?: string;
        message?: string;
      } | null;
      setError(
        maybeAny?.error ??
          maybeAny?.message ??
          "Wystąpił błąd podczas wysyłania.",
      );
    } finally {
      setIsUploading(false);
    }
  }

  return (
    <div className={"mx-auto mt-12 w-full max-w-[500px]"}>
      <div className="mb-6">
        <h3 className={"mb-1 text-center font-semibold"}>
          Możesz wystawiać recepty?
        </h3>
        <p className="text-muted-foreground mb-4 text-center text-sm font-normal">
          {isSent
            ? "Dokument został wysłany."
            : "Wyślij dokument poświadczający."}
        </p>
        {!file ? (
          <div className="relative flex items-center gap-4 rounded-xl border-2 border-dashed p-4 transition-all hover:cursor-pointer hover:bg-zinc-50">
            <div className="bg-muted flex h-11 min-w-11 items-center justify-center rounded-md p-2">
              <ImageIcon size={24} className="stroke-muted-foreground" />
            </div>
            <div>
              <div className="text-foreground mb-1.5 text-base font-bold">
                Przeciągnij plik lub kliknij
              </div>
              <div className="text-sm font-normal text-zinc-600">
                Obsługiwane formaty JPG, PNG | Maks. rozmiar 2 MB
              </div>
            </div>
            <input
              ref={inputRef}
              className="absolute top-0 left-0 h-full w-full opacity-0 hover:cursor-pointer"
              type="file"
              accept="image/*"
              disabled={isUploading || isSent}
              onChange={handleFileChange}
            />
          </div>
        ) : (
          <div className="relative flex items-center justify-between gap-4 rounded-xl border-2 border-dashed p-4">
            <div className="flex items-center gap-4">
              <div className="bg-muted flex max-w-[160px] items-center justify-center overflow-hidden rounded-md">
                <img src={file} className="w-full" alt="upload" />
              </div>
              <span className="text-sm text-zinc-600">{fileName}</span>
            </div>
            <Button
              onClick={removeFile}
              type="button"
              variant="ghost"
              size="icon"
              className="text-destructive hover:text-destructive"
              disabled={isUploading || isSent}
            >
              <X size={20} />
            </Button>
          </div>
        )}
      </div>

      <Button
        onClick={upload}
        disabled={!file || isUploading || isSent}
        className="mb-4 w-full"
      >
        {isUploading ? "Wysyłanie..." : isSent ? "Wysłano" : "Wyślij dokument"}
      </Button>
      {isSent && (
        <p className="mb-4 text-center text-sm font-medium text-teal-700">
          Dziękujemy! Zweryfikujemy dokument jak najszybciej i odblokujemy
          dostęp do treści.
        </p>
      )}
      <FormError error={error} />
    </div>
  );
};
